/**
 * NavigatorRibbon · the Course Navigator's ribbon (punchlist 2.9).
 *
 * Based word for word on CourseMapRibbon.jsx (its header follows), with these changes only:
 *   · the printed word can change in place: engine.setWord(w) rebuilds the lettering and draws once,
 *     without touching the cloth simulation (as in the MVP); the `word` prop drives it, and the
 *     engine is never recreated for a word change
 *   · it drops once per visit (later mounts hang still)
 *   · `expanded` sets aria-expanded; hover and keyboard focus lift it only while parked
 *   · `tooltip` shows after 650 ms of hover, only while parked
 *   · `onEngine` hands the engine to the caller (for tests)
 *   · it goes idle: below 0.01 px per step (the original 0.004 sat inside the solver's residual noise,
 *     so it almost never idled), with a safety cap of 4 s after the last wake; if the cap stops it before
 *     the cloth is still (slow devices), it finishes settling in idle time and draws the still pose once
 *   · the label fits: the hanging face uses the MVP's fitted stack, the flat face shrinks to fit
 *   · it appears only when the page is idle (about 1.5 s after the first lesson loads), so its drop never
 *     competes with the video starting; later lessons restore the settled pose saved from the first
 *     ribbon (no drop, no animation, no cost)
 */
/**
 * CourseMapRibbon — a satin ribbon bookmark that hangs from the page edge and opens the course map.
 *
 *   At rest it hangs down the right edge of its container, folded over the corner, with
 *   C O U R S E  M A P stacked upright on its back. Hover or keyboard focus smooths it out flat
 *   (reading "‹ COURSE MAP"); leaving lets gravity take it again. On mount it drops once.
 *
 *   The motion is a real cloth simulation (position-based dynamics: a sheet of particles with
 *   stretch, shear and bending constraints, gravity, air drag, and collisions with the page and
 *   with itself). It is drawn every frame on a <canvas> as a lit satin mesh: linear-light
 *   Lambert shading plus an anisotropic (Kajiya–Kay) sheen along the fibres, a filmic highlight
 *   roll-off, lit selvedges, a fibre texture and a cast shadow. The lettering is mapped onto
 *   the cloth, front and back, so it bends with the fabric.
 *
 *   The simulation only runs while the ribbon moves, and goes fully idle once it settles.
 *   prefers-reduced-motion: it lies flat and still. Keyboard focus lifts it like hover does.
 *
 * USAGE
 *   The ribbon hangs from the RIGHT edge of its nearest positioned ancestor, so put it inside a
 *   `position: relative` container that does not clip it (overflow: visible), e.g. the video
 *   page's search band. `top` is the distance from that container's top to the ribbon's top edge.
 *
 *     <div style={{ position: 'relative' }}>
 *       …search bar (keep it clear of the last ~230px on the right)…
 *       <CourseMapRibbon top={32} onClick={openCourseMap} ariaLabel="Open course map, lesson 3 of 6" />
 *     </div>
 *
 * PROPS
 *   onClick      () => void   what pressing the ribbon does (open the course navigator)
 *   label        string       printed on both sides; default "Course map"
 *   ariaLabel    string       accessible name; default "Open course map"
 *   top          number       px from the container's top to the ribbon's top edge; default 32
 *   hoverDelay   number       ms the pointer must rest before the ribbon lifts; default 60
 *   dropDelay    number       ms after mount before the first drop; default 550
 *   style        object       extra styles for the wrapper (e.g. zIndex)
 *
 * The lettering is set like the app's labels: DM Sans semibold in small tracked capitals, centred
 * on the ribbon; it waits for the font before printing it.
 */
import React, { useEffect, useRef, useState } from 'react';

// ── geometry (CSS px) ──────────────────────────────────────────────────────────────────
const RIBBON_LENGTH = 206;    // how far it reaches across the page when smoothed flat
const RIBBON_HEIGHT = 34;     // the ribbon's width
const NOTCH_DEPTH = 11;       // the V-notch at its tip
const CANVAS_W = RIBBON_LENGTH + 60;        // room for the swing toward the viewer and the shadow
const CANVAS_EXTRA_H = RIBBON_LENGTH + 70;  // below the ribbon's top edge: the hanging length plus shadow

// ── the engine: simulation + rendering. Returns { lift, fall, destroy }. ─────────────────
function createRibbon(cv, btn, { top, labelText, dropDelay, drop = true, pose = null, onIdle = null, unroll = false }) {
  let word = labelText;   // NavigatorRibbon: the printed word can change in place (setWord)
  const P = { W: CANVAS_W, H: top + CANVAS_EXTRA_H, TOTAL: RIBBON_LENGTH, h: RIBBON_HEIGHT, L: RIBBON_LENGTH - RIBBON_HEIGHT, NOTCH: NOTCH_DEPTH, RT: top };
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  cv.width = P.W * dpr; cv.height = P.H * dpr;
  const ctx = cv.getContext('2d');
  const sh = document.createElement('canvas'); sh.width = cv.width; sh.height = cv.height; const sctx = sh.getContext('2d');
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const C0 = P.L + P.h;

  // ── the cloth: rows across the width × columns from the crease to the tip ──────────────
  const R = 5, N = 34;                                   // particle grid
  const idx = (r, c) => r * (N + 1) + c;
  const parts = [];
  for (let r = 0; r < R; r++) {
    const t = r / (R - 1), y = t * P.h, xc = C0 - y, xmin = P.NOTCH * (1 - Math.abs(2 * t - 1));
    for (let c = 0; c <= N; c++) {
      const x = xc - (c / N) * (xc - xmin);
      parts.push({ x, y, z: 0, px: x, py: y, pz: 0, rx: x, ry: y, w: c === 0 ? 0 : 1, t });
    }
  }
  // NavigatorRibbon: a saved, settled pose (from an earlier ribbon this visit) hangs it at once, with no drop
  const restored = !!(pose && pose.length === parts.length);
  if (restored) pose.forEach((q, i) => { const p = parts[i]; p.x = p.px = q[0]; p.y = p.py = q[1]; p.z = p.pz = q[2]; });
  const cons = [];
  const link = (a, b, k) => { const A = parts[a], B = parts[b]; cons.push([a, b, Math.hypot(A.rx - B.rx, A.ry - B.ry), k]); };
  for (let r = 0; r < R; r++) for (let c = 0; c < N; c++) link(idx(r, c), idx(r, c + 1), 1);             // along the length: no stretch
  for (let c = 0; c <= N; c++) for (let r = 0; r < R - 1; r++) link(idx(r, c), idx(r + 1, c), 1);         // across: no stretch
  for (let r = 0; r < R - 1; r++) for (let c = 0; c < N; c++) { link(idx(r, c), idx(r + 1, c + 1), 1); link(idx(r + 1, c), idx(r, c + 1), 1); }  // shear
  for (let c = 0; c <= N; c++) link(idx(0, c), idx(R - 1, c), 1);                                         // stays flat across its width
  for (let r = 0; r < R; r++) for (let c = 0; c < N - 1; c++) link(idx(r, c), idx(r, c + 2), 0.28);       // satin's soft body along its length
  for (let r = 0; r < R; r++) for (let c = 0; c < N - 3; c++) link(idx(r, c), idx(r, c + 4), 0.07);

  const e = { raf: 0, last: 0, hold: 0, holdT: 0, held: false, quiet: 0 };
  const G = 2300, DRAG = 3.6;                             // px/s², 1/s
  const inFlat = (x, y) => x + y >= C0 - 0.5 && x <= P.TOTAL && y >= -1 && y <= P.h + 1;
  const nudge = (s) => { for (const p of parts) if (p.w) { const f = (C0 - (p.rx + p.ry)) / (C0 + P.h); p.pz -= s * f; } };
  const sim = (dt) => {
    const sub = 4, hs = dt / sub;
    e.hold += (e.holdT - e.hold) * (1 - Math.exp(-dt / (e.holdT ? 0.13 : 0.05)));
    for (let s = 0; s < sub; s++) {
      const drag = Math.exp(-(DRAG + e.hold * 22) * hs), KH = 620 * e.hold;
      for (const p of parts) {
        if (!p.w) continue;
        const vx = (p.x - p.px) * drag, vy = (p.y - p.py) * drag, vz = (p.z - p.pz) * drag;
        p.px = p.x; p.py = p.y; p.pz = p.z;
        const ax = KH * (p.rx - p.x), ay = G * (1 - e.hold) + KH * (p.ry - p.y), az = KH * (0 - p.z);
        p.x += vx + ax * hs * hs; p.y += vy + ay * hs * hs; p.z += vz + az * hs * hs;
      }
      for (let it = 0; it < 10; it++) {
        for (const [a, b, rest, k] of cons) {
          const A = parts[a], B = parts[b], dx = B.x - A.x, dy = B.y - A.y, dz = B.z - A.z, len = Math.hypot(dx, dy, dz) || 1e-6;
          const wsum = A.w + B.w; if (!wsum) continue;
          const corr = (len - rest) / len * k / wsum;
          A.x += dx * corr * A.w; A.y += dy * corr * A.w; A.z += dz * corr * A.w;
          B.x -= dx * corr * B.w; B.y -= dy * corr * B.w; B.z -= dz * corr * B.w;
        }
        for (const p of parts) { if (!p.w) continue; const zmin = inFlat(p.x, p.y) ? 1.4 : 0; if (p.z < zmin) { p.z = zmin; p.pz = zmin; p.px = p.x - (p.x - p.px) * 0.6; p.py = p.y - (p.y - p.py) * 0.6; } }
      }
    }
    let vmax = 0; for (const p of parts) vmax = Math.max(vmax, Math.abs(p.x - p.px), Math.abs(p.y - p.py), Math.abs(p.z - p.pz));
    return vmax;
  };

  // ── rendering: Catmull-Rom through the particles, then the lit satin mesh ────────────────
  const toCanvas = (x, y) => [P.W - P.TOTAL + x, P.RT + y];
  const persp = 640, pcx = P.W - P.TOTAL + C0 * 0.55, pcy = P.RT + P.h * 0.5;
  const project = (X, Y, Z) => { const [sx, sy] = toCanvas(X, Y); const k = persp / (persp - Z); return [pcx + (sx - pcx) * k, pcy + (sy - pcy) * k]; };
  const cr = (p0, p1, p2, p3, u) => { const u2 = u * u, u3 = u2 * u; return 0.5 * (2 * p1 + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u2 + (-p0 + 3 * p1 - 3 * p2 + p3) * u3); };
  const NS = 60, NT = 24;
  const grid = new Array((NS + 1) * (NT + 1));
  const tmp = new Array(R);
  const sampleRow = (r, u) => {                           // a point along row r at fraction u of its length
    const f = u * N, c = Math.min(N - 1, Math.floor(f)), uu = f - c;
    const g = (cc) => parts[idx(r, Math.max(0, Math.min(N, cc)))];
    const a = g(c - 1), b = g(c), c2 = g(c + 1), d = g(c + 2);
    return [cr(a.x, b.x, c2.x, d.x, uu), cr(a.y, b.y, c2.y, d.y, uu), cr(a.z, b.z, c2.z, d.z, uu), cr(a.rx, b.rx, c2.rx, d.rx, uu), cr(a.ry, b.ry, c2.ry, d.ry, uu)];
  };
  const build = () => {
    for (let i = 0; i <= NS; i++) {
      const u = Math.pow(i / NS, 1.35);
      for (let r = 0; r < R; r++) tmp[r] = sampleRow(r, u);
      for (let j = 0; j <= NT; j++) {
        const f = j / NT * (R - 1), r = Math.min(R - 2, Math.floor(f)), v = f - r;
        const g = (rr) => tmp[Math.max(0, Math.min(R - 1, rr))];
        const a = g(r - 1), b = g(r), c = g(r + 1), d = g(r + 2);
        const X = cr(a[0], b[0], c[0], d[0], v), Y = cr(a[1], b[1], c[1], d[1], v), Z = Math.max(0, cr(a[2], b[2], c[2], d[2], v));
        const [x, y] = project(X, Y, Z);
        const U = cr(a[3], b[3], c[3], d[3], v), V = cr(a[4], b[4], c[4], d[4], v);
        grid[j * (NS + 1) + i] = { x, y, X, Y, Z, U, V, t: j / NT };
      }
    }
  };
  const light = (() => { const v = [-0.42, -0.58, 0.70]; const m = Math.hypot(...v); return v.map((c) => c / m); })();
  const half = (() => { const v = [light[0], light[1], light[2] + 1]; const m = Math.hypot(...v); return v.map((c) => c / m); })();
  const STOPS = [[0, [118, 96, 56]], [.06, [174, 148, 96]], [.18, [220, 197, 147]], [.32, [242, 227, 188]], [.42, [255, 249, 230]],
                 [.52, [245, 231, 194]], [.68, [223, 201, 153]], [.84, [189, 164, 111]], [.95, [141, 117, 71]], [1, [108, 88, 50]]];
  const satin = (t) => { for (let k = 1; k < STOPS.length; k++) if (t <= STOPS[k][0]) { const [t0, c0] = STOPS[k - 1], [t1, c1] = STOPS[k]; const f = (t - t0) / (t1 - t0); return c0.map((c, i) => c + (c1[i] - c) * f); } return STOPS[STOPS.length - 1][1]; };
  const toLin = (c) => { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const toSrgb = (c) => { c = Math.max(0, Math.min(1, c)); return Math.round(255 * (c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055)); };
  const roll = (x) => x <= 0.8 ? x : 0.8 + 0.2 * (1 - Math.exp(-(x - 0.8) / 0.2));
  const lam = (n) => 0.5 + 0.55 * Math.max(0, n[0] * light[0] + n[1] * light[1] + n[2] * light[2]);
  const kk = (T) => { const th = T[0] * half[0] + T[1] * half[1] + T[2] * half[2]; return Math.pow(Math.sqrt(Math.max(0, 1 - th * th)), 90); };
  const LAM0 = lam([0, 0, 1]), KK0 = kk([1, 0, 0]);
  const fibre = (() => { const c = document.createElement('canvas'); c.width = c.height = 24; const g = c.getContext('2d');
    for (let i = -24; i < 48; i += 2) { g.strokeStyle = i % 4 ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'; g.lineWidth = 0.6; g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 8.4, 24); g.stroke(); }
    return ctx.createPattern(c, 'repeat'); })();
  const TS = 4;
  const label = (back) => {
    const c = document.createElement('canvas'); c.width = P.TOTAL * TS; c.height = P.h * TS; const g = c.getContext('2d'); g.scale(TS, TS);
    const text = String(word).toUpperCase(); let size = 10, track = 1.8;
    g.font = '600 ' + size + 'px "DM Sans", sans-serif'; g.textBaseline = 'middle';
    let widths = [...text].map((ch) => g.measureText(ch).width); let total = widths.reduce((a, b) => a + b, 0) + track * (text.length - 1);
    // NavigatorRibbon: fit the flat face's text (and its chevron) between the notch and the fold
    const fit = (P.TOTAL - P.h - 2) - P.NOTCH - 10;
    if (!back && total + 14 > fit) { const k = fit / (total + 14); size *= k; track *= k; g.font = '600 ' + size.toFixed(2) + 'px "DM Sans", sans-serif'; widths = [...text].map((ch) => g.measureText(ch).width); total = widths.reduce((a, b) => a + b, 0) + track * (text.length - 1); }
    const ink = (dx, dy, col) => { g.fillStyle = col; let x = 0; [...text].forEach((ch, i) => { g.fillText(ch, x + dx, P.h / 2 + 0.5 + dy); x += widths[i] + track; }); };
    g.save();
    if (back) {
      // each glyph is placed so that, after the fold mirrors the back over the crease, it stands upright on screen:
      // the glyph's x-axis maps to the ribbon's top edge (0,-1) and its y-axis toward the tip (-1,0)
      g.restore(); g.save();
      const letters = [...text], gap = 7;
      const nLetters = letters.filter((c) => c !== ' ').length, nGaps = letters.filter((c) => c === ' ').length;
      // NavigatorRibbon: the MVP's fitted stack, so long words fit (smaller letters when the spacing gets tight)
      const step = Math.min(13.4, (142 - nGaps * gap) / Math.max(1, nLetters - 1)), stackFont = step >= 13 ? 10.5 : 8.5;
      const spanC = (nLetters - 1) * step + nGaps * gap, midX = ((P.TOTAL - P.h - 4) + (P.NOTCH + 4)) / 2;
      let cx = midX + spanC / 2;   // first letter, so the stack is centred between the mitred top and the notch
      g.textAlign = 'center'; g.textBaseline = 'middle'; g.font = '600 ' + stackFont + 'px "DM Sans", sans-serif';
      const glyph = (ch, x, dx, dy, col) => { g.save(); g.setTransform(0, -TS, -TS, 0, (x + dy) * TS, (P.h / 2 - dx) * TS); g.fillStyle = col; g.fillText(ch, 0, 0.5); g.restore(); };
      for (const ch of letters) { if (ch === ' ') { cx -= gap; continue; }
        glyph(ch, cx, 0, 0.8, 'rgba(255,248,225,0.4)'); glyph(ch, cx, 0, 0, 'rgba(58,46,20,0.86)'); cx -= step; }
      g.restore(); return c;
    }
    else { const mid = (P.NOTCH + (P.TOTAL - P.h - 2)) / 2, full = total + 14; g.translate(mid - full / 2 + 14, 0);   // centred on the visible flat face, between the notch and the fold (NavigatorRibbon: was the whole length, which ran long words under the fold)
      g.strokeStyle = 'rgba(58,46,20,0.86)'; g.lineWidth = 1.3; g.lineCap = 'round'; g.lineJoin = 'round';
      g.beginPath(); g.moveTo(-10, P.h / 2 - 3.2); g.lineTo(-13.4, P.h / 2 + 0.3); g.lineTo(-10, P.h / 2 + 3.8); g.stroke(); }
    ink(0, 0.8, 'rgba(255,248,225,0.4)'); ink(0, 0, 'rgba(58,46,20,0.86)');
    g.restore(); return c;
  };
  let texFront = label(false), texBack = label(true);
  const FRONT_X = [P.NOTCH, P.TOTAL - P.h - 2], BACK_X = [P.NOTCH, P.TOTAL - P.h - 2];
  const texQuad = (img, s0, s1, s3, d0, d1, d2, d3) => {
    // affine map from three texture corners to three screen corners; clip to the (slightly grown) quad
    const [sx0, sy0] = s0, [sx1, sy1] = s1, [sx3, sy3] = s3;
    const den = (sx1 - sx0) * (sy3 - sy0) - (sx3 - sx0) * (sy1 - sy0); if (Math.abs(den) < 1e-6) return;
    const a = ((d1[0] - d0[0]) * (sy3 - sy0) - (d3[0] - d0[0]) * (sy1 - sy0)) / den, c2 = ((d3[0] - d0[0]) * (sx1 - sx0) - (d1[0] - d0[0]) * (sx3 - sx0)) / den;
    const b = ((d1[1] - d0[1]) * (sy3 - sy0) - (d3[1] - d0[1]) * (sy1 - sy0)) / den, d = ((d3[1] - d0[1]) * (sx1 - sx0) - (d1[1] - d0[1]) * (sx3 - sx0)) / den;
    const e2 = d0[0] - a * sx0 - c2 * sy0, f = d0[1] - b * sx0 - d * sy0;
    const cx = (d0[0] + d1[0] + d2[0] + d3[0]) / 4, cy = (d0[1] + d1[1] + d2[1] + d3[1]) / 4, grow = (p) => [p[0] + (p[0] - cx) * 0.06, p[1] + (p[1] - cy) * 0.06];
    ctx.save(); ctx.beginPath(); ctx.moveTo(...grow(d0)); ctx.lineTo(...grow(d1)); ctx.lineTo(...grow(d2)); ctx.lineTo(...grow(d3)); ctx.closePath(); ctx.clip();
    ctx.transform(a, b, c2, d, e2, f); ctx.drawImage(img, 0, 0, P.TOTAL, P.h); ctx.restore();
  };
  const flatPath = (c) => { const A = project(C0, 0, 0), B = project(P.TOTAL, 0, 0), Cc = project(P.TOTAL, P.h, 0), D = project(C0 - P.h, P.h, 0);
    c.beginPath(); c.moveTo(...A); c.lineTo(...B); c.lineTo(...Cc); c.lineTo(...D); c.closePath(); };
  const draw = () => {
    build();
    const cells = [];
    for (let j = 0; j < NT; j++) for (let i = 0; i < NS; i++) {
      const a = grid[j * (NS + 1) + i], b = grid[j * (NS + 1) + i + 1], c = grid[(j + 1) * (NS + 1) + i + 1], dd = grid[(j + 1) * (NS + 1) + i];
      const ux = c.X - a.X, uy = c.Y - a.Y, uz = c.Z - a.Z, vx = b.X - dd.X, vy = b.Y - dd.Y, vz = b.Z - dd.Z;
      let n = [uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx]; const m = Math.hypot(...n) || 1; n = n.map((q) => q / m);
      const facing = n[2]; if (n[2] < 0) n = n.map((q) => -q);
      let T = [b.X - a.X + c.X - dd.X, b.Y - a.Y + c.Y - dd.Y, b.Z - a.Z + c.Z - dd.Z]; const tm = Math.hypot(...T) || 1; T = T.map((q) => q / tm);
      cells.push({ a, b, c, d: dd, z: (a.Z + b.Z + c.Z + dd.Z) / 4, n, T, t: (a.t + c.t) / 2, back: facing < 0 });
    }
    cells.sort((p, q) => p.z - q.z);
    sctx.setTransform(1, 0, 0, 1, 0, 0); sctx.clearRect(0, 0, sh.width, sh.height); sctx.setTransform(dpr, 0, 0, dpr, 0, 0); sctx.fillStyle = '#000';
    sctx.save(); sctx.translate(-1.5, 3); flatPath(sctx); sctx.fill(); sctx.restore();
    for (const q of cells) { const o = (p) => [p.x - 1.5 - p.Z * 0.35, p.y + 3 + p.Z * 0.55];
      sctx.beginPath(); sctx.moveTo(...o(q.a)); sctx.lineTo(...o(q.b)); sctx.lineTo(...o(q.c)); sctx.lineTo(...o(q.d)); sctx.closePath(); sctx.fill(); }
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.save(); ctx.globalAlpha = 0.5; ctx.filter = 'blur(' + (5 * dpr) + 'px)'; ctx.drawImage(sh, 0, 0); ctx.restore();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const g = ctx.createLinearGradient(0, project(0, 0, 0)[1], 0, project(0, P.h, 0)[1]);
    for (const [t, c] of STOPS) g.addColorStop(t, 'rgb(' + c.map(Math.round).join(',') + ')');
    flatPath(ctx); ctx.fillStyle = g; ctx.fill();
    const [x0, y0] = project(C0, 0, 0), [x1] = project(P.TOTAL, 0, 0), [xb, yb] = project(C0 - P.h, P.h, 0);
    ctx.fillStyle = 'rgba(255,248,226,0.6)'; ctx.fillRect(x0, y0, x1 - x0, 0.8);
    ctx.fillStyle = 'rgba(30,22,8,0.5)'; ctx.fillRect(xb, yb - 0.8, x1 - xb, 0.8);
    // contact shade on the flat run where the fold lies on it (from how far the tail has come over)
    let over = 0; for (const p of parts) if (p.w && inFlat(p.x, p.y) && p.z > 0.8) over++;
    const f = Math.min(1, over / (parts.length * 0.3));
    if (f > 0.01) { const sg = ctx.createLinearGradient(x0 - P.h * 0.5, 0, x0 + P.h * 1.4, 0);
      sg.addColorStop(0, 'rgba(20,14,4,' + (0.5 * f) + ')'); sg.addColorStop(1, 'rgba(20,14,4,0)');
      ctx.save(); flatPath(ctx); ctx.clip(); ctx.fillStyle = sg; ctx.fillRect(xb - 4, y0 - 2, P.h * 2.6, P.h + 4); ctx.restore(); }
    for (const q of cells) {
      const base = satin(q.t), k = lam(q.n) / LAM0 * (q.back ? 0.78 : 1);
      const sheen = Math.max(-0.12, kk(q.T) - KK0) * (q.back ? 0.35 : 0.62);
      const col = base.map((c) => toSrgb(roll(toLin(c) * k + sheen * 0.85)));
      ctx.fillStyle = 'rgb(' + col.join(',') + ')'; ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = 0.35;
      ctx.beginPath(); ctx.moveTo(q.a.x, q.a.y); ctx.lineTo(q.b.x, q.b.y); ctx.lineTo(q.c.x, q.c.y); ctx.lineTo(q.d.x, q.d.y); ctx.closePath(); ctx.fill(); ctx.stroke();
    }
    const BI = 3, BJ = 4;
    for (let j = 0; j < NT; j += BJ) for (let i = 0; i < NS; i += BI) {
      const i2 = Math.min(NS, i + BI), j2 = Math.min(NT, j + BJ);
      const g0 = grid[j * (NS + 1) + i], g1 = grid[j * (NS + 1) + i2], g2 = grid[j2 * (NS + 1) + i2], g3 = grid[j2 * (NS + 1) + i];
      const um = (g0.U + g1.U + g2.U + g3.U) / 4;
      const sx = (g1.x - g0.x) * (g3.y - g0.y) - (g1.y - g0.y) * (g3.x - g0.x);
      const back = sx > 0;
      const R2 = back ? BACK_X : FRONT_X; if (um < R2[0] || um > R2[1]) continue;
      if (e.unrolling && (g0.Z + g1.Z + g2.Z + g3.Z) / 4 > 0.6) continue;   // lettering appears as the ribbon lies flat (no ink bleeding through the roll)
      texQuad(back ? texBack : texFront, [g0.U, g0.V], [g1.U, g1.V], [g3.U, g3.V], [g0.x, g0.y], [g1.x, g1.y], [g2.x, g2.y], [g3.x, g3.y]);
    }
    const edgeLine = (row, style) => { ctx.beginPath(); for (let i = 0; i <= NS; i++) { const q = grid[row * (NS + 1) + i]; if (i) ctx.lineTo(q.x, q.y); else ctx.moveTo(q.x, q.y); } ctx.strokeStyle = style; ctx.lineWidth = 0.7; ctx.stroke(); };
    edgeLine(0, 'rgba(255,250,236,0.55)'); edgeLine(NT, 'rgba(40,30,12,0.45)');
    ctx.save(); flatPath(ctx); ctx.clip(); ctx.globalCompositeOperation = 'soft-light'; ctx.fillStyle = fibre; ctx.fillRect(xb - 2, y0 - 2, P.TOTAL + 4, P.h + 4); ctx.restore();
  };

  // ── the loop ─────────────────────────────────────────────────────────────────────────────
  const step = (time) => {
    const dt = Math.min(1 / 30, (time - (e.last || time)) / 1000 || 1 / 60); e.last = time;
    const vmax = sim(dt);
    draw();
    // NavigatorRibbon: idle below 0.01 px per step (invisible). The original 0.004 sat inside the solver's own
    // residual noise (measured 0.0035–0.0045), so the cloth almost never went idle. Safety cap: idle anyway
    // 4 s after the last wake, holding the last drawn frame.
    if (!e.wokeAt) e.wokeAt = time;
    e.quiet = vmax < 0.01 && Math.abs(e.hold - e.holdT) < 0.002 ? e.quiet + 1 : 0;
    if (e.quiet > 20 || time - e.wokeAt > 4000) {
      const still = e.quiet > 20;
      e.raf = 0; e.last = 0; e.wokeAt = 0;
      if (still) { if (onIdle) onIdle(e); } else settleInIdle();   // the cap stopped it before the cloth was still
      return;
    }
    e.raf = requestAnimationFrame(step);
  };
  // NavigatorRibbon: when the 4 s cap stops the animation before the cloth is still (on a slow device frames arrive late,
  // and each frame simulates at most 1/30 s, so less cloth time passes than real time), finish settling in idle time:
  // simulation only, 12 steps per slice, at most 240 steps, until it moves under 0.002 px per step; then draw once and
  // report idle, so the ribbon ends hanging still and the pose saved for later lessons is truly at rest.
  const onIdleTime = (f) => (typeof window !== 'undefined' && window.requestIdleCallback ? window.requestIdleCallback(f, { timeout: 500 }) : setTimeout(f, 50));
  const cancelIdleTime = (id) => { if (typeof window !== 'undefined' && window.cancelIdleCallback) window.cancelIdleCallback(id); clearTimeout(id); };
  const settleInIdle = () => {
    let steps = 0;
    const slice = () => {
      e.settle = 0;
      if (e.raf || e.held) return;   // woken again (hover, focus): the animation takes over
      let v = 1;
      for (let i = 0; i < 12 && steps < 240; i++, steps++) { v = sim(1 / 60); if (v < 0.002) break; }
      if (v < 0.002 || steps >= 240) { draw(); if (onIdle) onIdle(e); return; }
      e.settle = onIdleTime(slice);
    };
    e.settle = onIdleTime(slice);
  };
  const wake = () => { if (e.settle) { cancelIdleTime(e.settle); e.settle = 0; } e.wokeAt = 0; if (!e.raf) { e.last = 0; e.quiet = 0; e.raf = requestAnimationFrame(step); } };
  e.sim = sim; e.draw = draw; e.parts = parts; e.nudge = nudge;
  // ── the unroll: the ribbon unwinds out of the page edge like a scroll ────────────────────────────────────────────
  // The roll is drawn as its own lit cylinder (column by column: the ribbon's satin colours and lighting model, an
  // anisotropic sheen wrapping around it, a specular streak, rim definition, weave lines that sweep across as it turns,
  // a contact shadow and ambient occlusion). The unrolled part is flat satin drawn exactly as the renderer draws flat
  // satin, so the hand-over to the cloth (and its drop) is seamless. The cloth mesh is not drawn while unrolling.
  const R_MIN = 5, THICK = 3.0, X_END = 0;
  const rFor = (rem) => Math.sqrt(rem * THICK / Math.PI) + R_MIN * Math.min(1, rem / 28);
  const X_START = P.TOTAL + rFor(P.TOTAL) + 3;   // waiting just off the page: the whole ribbon rolled up beyond the edge
  const rgbAt = (t, k, sheen) => satin(t).map((c) => toSrgb(roll(toLin(c) * k + sheen * 0.85)));
  e.drawUnroll = (u) => {
    const Xr = X_START + (X_END - X_START) * u;                        // where the roll touches the ribbon (ribbon x)
    const remaining = Math.max(0, Xr - 0);
    // the core shrinks away with the last turn, so the tip unfurls instead of a roll vanishing
    const r = rFor(Math.min(P.TOTAL, remaining));
    const [cx] = toCanvas(Xr, 0), yT = P.RT, yB = P.RT + P.h;
    const leftX = Math.max(Xr, 0);
    // the flat polygon: from the roll to the mitred crease (the stub beyond the crease is flatPath)
    const flatPoly = (c, dx = 0, dy = 0, fresh = true) => {
      if (fresh) c.beginPath();
      const L = Math.min(P.TOTAL, leftX); if (L >= P.TOTAL) return;
      const pts = [[L, 0], [P.TOTAL, 0], [P.TOTAL, P.h], [L, P.h]];
      if (Xr < P.NOTCH) { const q = 1 - Math.max(0, Xr) / P.NOTCH;   // past the notch: the flat edge follows the V exactly
        pts.push([L, P.h * (1 + q) / 2], [P.NOTCH, P.h / 2], [L, P.h * (1 - q) / 2]); }
      pts.forEach(([x, y], i) => { const [X, Y] = toCanvas(x, y); i ? c.lineTo(X + dx, Y + dy) : c.moveTo(X + dx, Y + dy); });
      c.closePath();
    };
    // shadow layer: flat ribbon + stub + the roll (lifted, so its shadow falls further)
    sctx.setTransform(1, 0, 0, 1, 0, 0); sctx.clearRect(0, 0, sh.width, sh.height); sctx.setTransform(dpr, 0, 0, dpr, 0, 0); sctx.fillStyle = '#000';
    sctx.save(); sctx.translate(-1.5, 3); flatPoly(sctx); sctx.fill(); sctx.restore();
    { const qs = Xr < P.NOTCH ? 1 - Math.max(0, Xr) / P.NOTCH : 0; sctx.beginPath(); sctx.ellipse(cx - 1.5 - r * 0.35, (yT + yB) / 2 + 3 + r * 0.5, r * 1.05, P.h * 0.62 * (1 - qs * 0.6), 0, 0, Math.PI * 2); sctx.fill(); }
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.save(); ctx.globalAlpha = 0.5; ctx.filter = 'blur(' + (5 * dpr) + 'px)'; ctx.drawImage(sh, 0, 0); ctx.restore();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // the unrolled part: the real renderer's own flat ribbon (rendered once at the start), revealed up to the roll,
    // so it is pixel for pixel what the cloth starts its drop from
    ctx.save(); flatPoly(ctx); ctx.clip(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(e.flatImg, 0, 0); ctx.restore();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // ambient occlusion where the roll sits on the flat ribbon (to its right) and on the page (to its left)
    const ao = ctx.createLinearGradient(cx + r * 0.6, 0, cx + r + 12, 0); ao.addColorStop(0, 'rgba(25,18,6,0.42)'); ao.addColorStop(1, 'rgba(25,18,6,0)');
    ctx.save(); flatPoly(ctx); ctx.clip(); ctx.fillStyle = ao; ctx.fillRect(cx, yT, r + 14, P.h); ctx.restore();
    // the roll: a lit cylinder, column by column (its outer face is the ribbon's back)
    if (r < 0.6) return;
    // which parts of the ribbon's width are still rolled: all of it, or, once past the notch, only the two corner points
    const qn = Xr < P.NOTCH ? 1 - Math.max(0, Xr) / P.NOTCH : 0;
    const bands = qn > 0 ? [[yT, yT + P.h * (1 - qn) / 2], [yT + P.h * (1 + qn) / 2, yB]] : [[yT, yB]];
    const cols = Math.max(8, Math.ceil(2 * r * dpr)), w = (2 * r) / cols;
    const rot = remaining / Math.max(1, r);                               // how far it has turned (radians), for the weave
    for (let i = 0; i < cols; i++) {
      const xs = cx - r + (i + 0.5) * w, sa = Math.max(-0.999, Math.min(0.999, (xs - cx) / r)), ca = Math.sqrt(1 - sa * sa);
      const n = [sa, 0, ca], T = [ca, 0, -sa];
      const k = lam(n) / LAM0 * 0.94, sheen = Math.max(-0.12, kk(T) - KK0) * 0.38;
      const spec = Math.pow(Math.max(0, n[0] * half[0] + n[2] * half[2]), 34) * 0.3;
      const cg = ctx.createLinearGradient(0, yT, 0, yB);
      for (const [t] of STOPS) { const c = rgbAt(t, k, sheen + spec); cg.addColorStop(t, 'rgb(' + c.join(',') + ')'); }
      ctx.fillStyle = cg; for (const [b0, b1] of bands) ctx.fillRect(xs - w / 2 - 0.15, b0 - (b0 === yT ? 0.4 : 0), w + 0.3, b1 - b0 + (b1 === yB ? 0.4 : 0));
      // weave lines turning with the roll
      const ang = Math.asin(sa) + rot, wv = Math.cos(ang * 7);
      if (wv > 0.93) { ctx.fillStyle = 'rgba(255,250,232,' + (0.16 * ca) + ')'; for (const [b0, b1] of bands) ctx.fillRect(xs - w / 2, b0, w, b1 - b0); }
      else if (wv < -0.93) { ctx.fillStyle = 'rgba(40,28,8,' + (0.10 * ca) + ')'; for (const [b0, b1] of bands) ctx.fillRect(xs - w / 2, b0, w, b1 - b0); }
    }
    // rim definition and the roll's selvedges
    const rim = ctx.createLinearGradient(cx - r, 0, cx + r, 0);
    rim.addColorStop(0, 'rgba(30,22,8,0.55)'); rim.addColorStop(0.12, 'rgba(30,22,8,0)'); rim.addColorStop(0.86, 'rgba(30,22,8,0)'); rim.addColorStop(1, 'rgba(30,22,8,0.5)');
    ctx.fillStyle = rim; for (const [b0, b1] of bands) ctx.fillRect(cx - r, b0, 2 * r, b1 - b0);
    ctx.fillStyle = 'rgba(255,250,236,0.6)'; ctx.fillRect(cx - r * 0.8, yT - 0.4, r * 1.6, 0.8);
    ctx.fillStyle = 'rgba(40,30,12,0.55)'; ctx.fillRect(cx - r * 0.8, yB - 0.4, r * 1.6, 0.8);
    if (qn > 0) { ctx.fillStyle = 'rgba(40,30,12,0.4)'; ctx.fillRect(cx - r * 0.8, bands[0][1] - 0.5, r * 1.6, 0.6); ctx.fillStyle = 'rgba(255,250,236,0.45)'; ctx.fillRect(cx - r * 0.8, bands[1][0], r * 1.6, 0.6); }
  };
  const bez = (x1, y1, x2, y2) => (t) => { let u = t; for (let i = 0; i < 8; i++) { const x = 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u - t; const dx = 3 * (1 - u) * (1 - u) * x1 + 6 * (1 - u) * u * (x2 - x1) + 3 * u * u * (1 - x2); if (Math.abs(dx) < 1e-6) break; u -= x / dx; } return 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u; };
  // NavigatorRibbon: change the printed word in place, without touching the cloth (as in the MVP)
  e.setWord = (w) => { word = w; texFront = label(false); texBack = label(true); draw();
    if (e.unrolling && e.flatImg) { const fg = e.flatImg.getContext('2d'); fg.clearRect(0, 0, cv.width, cv.height); fg.drawImage(cv, 0, 0); } };
  e.snapshot = () => parts.map((p) => [p.x, p.y, p.z]);   // the current pose, to restore on later mounts
  // during the entrance, hover and focus only record the state; the roll finishes, then the ribbon goes to it
  e.lift = () => { e.held = true; e.holdT = 1; if (btn) btn.setAttribute('data-open', ''); if (!reduce && !e.unrolling) wake(); };
  e.fall = () => { e.held = false; e.holdT = 0; if (btn) btn.removeAttribute('data-open'); if (!reduce && !e.unrolling) { nudge(0.35); wake(); } };
  if (unroll && !reduce) {
    e.unrolling = true;
    draw(); e.flatImg = document.createElement('canvas'); e.flatImg.width = cv.width; e.flatImg.height = cv.height; e.flatImg.getContext('2d').drawImage(cv, 0, 0);
    // the ease keeps a little speed at the very end, so the roll flows straight into the fall (no dead stop)
    const ROLL_MS = 1450, OUT = bez(0.3, 0, 0.5, 0.88);
    let t0 = 0;
    const tick = (time) => {
      if (!t0) t0 = time;
      const el = time - t0;
      const k = Math.max(0, Math.min(1, el / ROLL_MS));
      e.drawUnroll(OUT(k));
      if (k < 1) { e.uraf = requestAnimationFrame(tick); return; }
      // the hand-over: the ribbon is flat exactly where the cloth starts; the drop begins on the very next frame
      for (const p of parts) { p.x = p.px = p.rx; p.y = p.py = p.ry; p.z = p.pz = 0; }
      e.unrolling = false; e.uraf = 0;
      if (!e.held) { nudge(0.35); e.raf = requestAnimationFrame(step); } else wake();   // held (hovered) during the roll: it lifts
    };
    e.uraf = requestAnimationFrame(tick);
  } else draw();
  if (unroll && !reduce) { /* the drop follows the unroll */ }
  else if (reduce) { if (btn) btn.setAttribute('data-open', ''); }
  else if (drop && !restored) e.drop = setTimeout(() => { if (!e.held) { nudge(0.35); wake(); } }, dropDelay);   // NavigatorRibbon: after the slide-in, every lesson
  e.destroy = () => { cancelAnimationFrame(e.uraf); cancelAnimationFrame(e.raf); clearTimeout(e.drop); if (e.settle) { cancelIdleTime(e.settle); e.settle = 0; } e.raf = 0; };
  return e;
  
}


export default function NavigatorRibbon({
  onClick,
  label = 'Course Navigator',
  word,
  expanded = false,
  tooltip,
  onEngine,
  quietFocus,   // a ref: set .current = true just before returning focus here, so that focus doesn't lift it
  ariaLabel = 'Open Course Navigator',
  top = 32,
  hoverDelay = 60,
  dropDelay = 550,
  replayKey,   // a new value (each lesson) plays the entrance again: slide in, then fall
  style,
}) {
  const canvasRef = useRef(null);
  const buttonRef = useRef(null);
  const engineRef = useRef(null);
  const [started, setStarted] = useState(false);   // hidden until the engine exists (see the idle start)
  const hoverTimer = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setStarted(false);
    const start = () => {
      if (cancelled || !canvasRef.current || !buttonRef.current) return;
      // the entrance, every time (each lesson is a new video): rolled up just off the page, it rolls in and unwinds to the
      // tip, then falls straight into its drop
      engineRef.current = createRibbon(canvasRef.current, buttonRef.current, { top, labelText: word || label, dropDelay, drop: true, pose: null, unroll: true });
      setStarted(true);
      if (onEngine) onEngine(engineRef.current);
    };
    // print the lettering in DM Sans: wait for the font if the browser can tell us when it's ready
    const whenFontReady = () => { if (typeof document !== 'undefined' && document.fonts && document.fonts.load) {
      Promise.all([document.fonts.load('600 10px "DM Sans"'), document.fonts.load('600 10.5px "DM Sans"')]).then(start, start);
    } else start(); };
    // NavigatorRibbon: it enters as soon as the page shows. Rolling in is cheap (about 1.5 ms a frame); the drop, the
    // costly part, only starts once the roll has finished (about 1.5 s in), after the page is up.
    let idleId = 0, waitId = 0;
    whenFontReady();
    return () => {
      cancelled = true;
      clearTimeout(waitId); if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      clearTimeout(hoverTimer.current);
      if (engineRef.current) engineRef.current.destroy();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, label, dropDelay, replayKey]);

  // the word changes in place (COURSE NAVIGATOR ↔ CLOSE): never recreate the engine
  const shownWord = useRef(word || label);
  useEffect(() => {
    const w = word || label;
    if (engineRef.current && w !== shownWord.current) { engineRef.current.setWord(w); shownWord.current = w; }
  }, [word, label]);

  // the tooltip shows after a pause on hover, only while the ribbon is parked
  const [tip, setTip] = useState(false);
  const tipTimer = useRef(0);
  useEffect(() => { if (expanded) { clearTimeout(tipTimer.current); setTip(false); } }, [expanded]);
  useEffect(() => () => clearTimeout(tipTimer.current), []);

  // Touch screens have no hover: Safari turns a first tap into a hover, and a hover that changes the screen swallows the
  // tap. So on touch the ribbon is a plain toggle: one tap opens, one tap closes; no lift, no tooltip.
  const touchOnly = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches;
  const lift = () => {
    if (touchOnly) return;
    if (expanded) return;   // hover lifts only while parked
    clearTimeout(hoverTimer.current);
    if (tooltip) { clearTimeout(tipTimer.current); tipTimer.current = setTimeout(() => setTip(true), 650); }
    hoverTimer.current = setTimeout(() => engineRef.current && engineRef.current.lift(), hoverDelay);
  };
  const liftNow = () => { if (quietFocus && quietFocus.current) { quietFocus.current = false; return; } if (touchOnly || expanded) return; clearTimeout(hoverTimer.current); if (engineRef.current) engineRef.current.lift(); };
  const fall = () => { clearTimeout(hoverTimer.current); clearTimeout(tipTimer.current); setTip(false); if (engineRef.current && engineRef.current.held) engineRef.current.fall(); };

  // One fixed clickable area, whatever the ribbon is doing: the hanging part AND where the ribbon lies when it is lifted
  // straight. It never changes during the animation, so the cursor never has to chase the ribbon.
  const hang = RIBBON_HEIGHT + 8;
  return (
    <div style={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, ...style }}>
      <style>{`
        .cmr-btn { clip-path: polygon(0 0, 100% 0, 100% 100%, calc(100% - ${hang}px) 100%, calc(100% - ${hang}px) ${RIBBON_HEIGHT + 6}px, 0 ${RIBBON_HEIGHT + 6}px); }
        .cmr-btn:focus-visible .cmr-ring { opacity: 1; }
      `}</style>
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{ position: 'absolute', top: 0, right: 0, width: CANVAS_W, height: top + CANVAS_EXTRA_H, pointerEvents: 'none', zIndex: 29 }}
      />
      <button
        ref={buttonRef}
        type="button"
        className="cmr-btn"
        aria-label={ariaLabel}
        aria-expanded={expanded ? 'true' : 'false'}
        onClick={onClick}
        onMouseEnter={lift}
        onMouseLeave={fall}
        onFocus={liftNow}
        onBlur={fall}
        style={{
          position: 'absolute', top, right: 0, width: RIBBON_LENGTH, height: RIBBON_LENGTH + 10,
          padding: 0, border: 0, background: 'transparent', cursor: 'pointer', zIndex: 30, visibility: started ? 'visible' : 'hidden',
          outline: 'none', WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span
          className="cmr-ring"
          aria-hidden="true"
          style={{ position: 'absolute', top: -4, right: 0, width: hang, height: RIBBON_LENGTH + 10, borderRadius: 5,
            boxShadow: 'inset 0 0 0 2px #E8D5A3', opacity: 0, pointerEvents: 'none', transition: 'opacity .2s ease' }}
        />
      </button>
      {tooltip && tip && !expanded && (
        <div role="tooltip" style={{ position: 'absolute', top: top + 48, right: hang + 10, zIndex: 31, padding: '7px 12px', borderRadius: 8, background: '#F5F5F0', color: '#141414',
          fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', boxShadow: '0 10px 28px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>{tooltip}</div>
      )}
    </div>
  );
}

